import { useDispatch, useSelector } from "react-redux";
import { PrimaryButton, MUTED } from "../FlightFinalOffer/ui";
import { Dispatch, startTransition, useState } from "react";
import * as Actions from "../store/actions";
import { errorMessage, formatCurrency } from "../util/Helpers";
//import { useFlutterwave } from "flutterwave-react-v3";
import { loadStripe } from "@stripe/stripe-js";
import LocalGateway from "./LocalGateway";
import { usePaystackPayment } from "react-paystack";

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_KEY);

// Pay-later checkout for a held fare. The transaction row (not the upstream
// hold query) is the source of truth for what's charged: `amount` is the
// grand total and `currency` picks the gateway — Paystack for NGN, Stripe
// for international currencies (USD, CAD), matching vigo's
// CURRENCY_VENDOR_MAP.
const PaymentForm = ({ trans, onPaid }: { trans: any; onPaid: () => void }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [stripeClientSecret, setStripeClientSecret] = useState<string | null>(
    null,
  );
  const [isStartingStripe, setIsStartingStripe] = useState(false);
  const dispatch: Dispatch<any> = useDispatch();
  // intentGateway / verifyPayment drive the order slice's loading flag.
  const loading = useSelector((state: any) => state.order.loading);
  const total = Number(trans.amount) || 0;
  const vendor = trans.currency === "NGN" ? "paystack" : "stripe";

  const pconfig = {
    reference: trans.systemRef,
    email: trans?.email,
    currency: trans.currency,
    // Paystack requires an integer kobo amount — parseFloat(...) * 100 can
    // land on a non-integer float (e.g. 1500055.0000000002) and Paystack
    // rejects that outright ("Attribute amount must be a valid integer").
    amount: Math.round(total * 100),
    publicKey: import.meta.env.VITE_PAYSTACK_KEY,
    metadata: {
      service: "vigo",
      systemRef: trans?.systemRef,
      custom_fields: [],
    },
  };
  const initializePayment = usePaystackPayment(pconfig);

  // Tells vigo the traveler paid for the held fare. VerifyPaymentDto wants
  // `hold` as the literal string "false" (not a boolean) — omitting it
  // defaults to hold=true, which would mark the booking done without ever
  // issuing the ticket. The gateway webhook then confirms the charge and
  // issues the ticket.
  const confirmPaid = (paidVendor: string, processRef?: string) => {
    startTransition(() => {
      dispatch(
        Actions.verifyPayment(
          {
            systemRef: trans.systemRef,
            processRef,
            vendor: paidVendor,
            email: trans.email,
            phoneNumber: trans.phone,
            hold: "false",
          },
          () => onPaid(),
          () => {
            errorMessage(
              "Could not complete your transaction, contact adminstrator",
            );
          },
        ),
      );
    });
  };

  // A held fare has no payment intent yet (hold() never touches the
  // gateway), so create one now — same as the pay-now checkout.
  const startStripePayment = () => {
    setIsStartingStripe(true);
    dispatch(
      Actions.intentGateway(
        {
          vendor: "stripe",
          systemRef: trans.systemRef,
          narration: `Held flight ${trans.systemRef}`,
        },
        (res: any) => {
          setIsStartingStripe(false);
          if (res?.model?.message) {
            setStripeClientSecret(res.model.message);
            setIsOpen(true);
          } else {
            errorMessage("Could not start payment, please try again.");
          }
        },
        () => {
          setIsStartingStripe(false);
          errorMessage("Could not start payment, please try again.");
        },
      ),
    );
  };

  return (
    <div className="co-card p-5 md:p-6">
      <LocalGateway
        isOpen={isOpen}
        clientSecret={stripeClientSecret}
        setIsOpen={setIsOpen}
        stripePromise={stripePromise}
        onPaid={(intentId: string) => {
          setIsOpen(false);
          confirmPaid("stripe", intentId);
        }}
      />
      <PrimaryButton
        loading={loading || isStartingStripe}
        onClick={() => {
          if (vendor === "paystack") {
            initializePayment({
              onSuccess: (res: any) => confirmPaid("paystack", res?.reference),
            });
          } else {
            startStripePayment();
          }
        }}
      >
        {`Pay ${formatCurrency(total, trans.currency)}`}
      </PrimaryButton>
      <p className="text-xs text-center mt-3" style={{ color: MUTED }}>
        Secure payment via {vendor === "paystack" ? "Paystack" : "Stripe"}
      </p>
    </div>
  );
};
export default PaymentForm;
